import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import Layout from '../../components/Layout';
import DatePicker from '../../components/DatePicker';
import Loading from '../../components/Loading';
import {
  fetchHistory,
  setFilters,
  setCurrentPage,
} from '../../store/slices/historySlice';
import { fetchCouponTypes } from '../../store/slices/couponTypeSlice';
import './History.css';

const ITEMS_PER_PAGE = 10;

const History = () => {
  const dispatch = useDispatch();
  const { historyData, filters, currentPage, isLoading } = useSelector((state) => state.history);
  const { couponTypes } = useSelector((state) => state.couponType);

  useEffect(() => {
    dispatch(fetchHistory(filters));
    dispatch(fetchCouponTypes());
  }, []);

  const totalPages = Math.ceil(historyData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentData = historyData.slice(startIndex, endIndex);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const typeOptions = useMemo(() => [
    { value: 'all', label: 'すべて' },
    ...couponTypes.map(t => ({ value: t.typeCode, label: `${t.name} (${t.typeCode})` }))
  ], [couponTypes]);

  const selectedType = typeOptions.find(o => o.value === filters.type) || typeOptions[0];

  const handleSearch = () => {
    dispatch(fetchHistory(filters));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      dispatch(setCurrentPage(page));
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage, '...', totalPages);
      }
    }

    return pages;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case '使用済み':
        return 'history__status--used';
      case '期限切れ':
        return 'history__status--expired';
      default:
        return '';
    }
  };

  return (
    <Layout>
      <div className="history">
        <div className="history__card">
          <h1 className="history__title">履歴確認</h1>

          <div className="history__filters">
            <div className="history__filter-row">
              <label className="history__filter-label">クライアントID</label>
              <input
                type="text"
                className="history__filter-input"
                placeholder="クライアントID"
                value={filters.clientId}
                onChange={(e) => handleFilterChange('clientId', e.target.value)}
              />
            </div>

            <div className="history__filter-row">
              <label className="history__filter-label">期間</label>
              <div className="history__filter-date-range">
                <DatePicker
                  value={filters.startDate}
                  onChange={(value) => handleFilterChange('startDate', value)}
                />
                <span className="history__filter-date-separator">~</span>
                <DatePicker
                  value={filters.endDate}
                  onChange={(value) => handleFilterChange('endDate', value)}
                />
              </div>
            </div>

            <div className="history__filter-row">
              <label className="history__filter-label">種類</label>
              <Select
                className="history__filter-select-container"
                options={typeOptions}
                value={selectedType}
                onChange={(opt) => handleFilterChange('type', opt.value)}
                maxMenuHeight={160}
                isSearchable={false}
              />

              <button 
                className="history__search-btn" 
                onClick={handleSearch}
                disabled={isLoading}
              >
                {isLoading ? '検索中...' : '検索'}
              </button>
            </div>
          </div>

          {isLoading && <Loading text="履歴を読み込み中..." />}

          {!isLoading && (
            <div className="history__table-wrapper">
              <table className="history__table">
                <thead className="history__table-head">
                  <tr className="history__table-row">
                    <th className="history__table-th history__table-th--number">番号</th>
                    <th className="history__table-th">クーポンコード</th>
                    <th className="history__table-th">クライアントID</th>
                    <th className="history__table-th">クーポン種類</th>
                    <th className="history__table-th">発行日</th>
                    <th className="history__table-th">有効期限</th>
                    <th className="history__table-th">ステータス</th>
                  </tr>
                </thead>
                <tbody className="history__table-body">
                  {currentData.map((item, index) => (
                    <tr key={item.id} className="history__table-row">
                      <td className="history__table-td history__table-td--number">
                        {startIndex + index + 1}
                      </td>
                      <td className="history__table-td">{item.couponCode}</td>
                      <td className="history__table-td">{item.clientId}</td>
                      <td className="history__table-td">{item.type}</td>
                      <td className="history__table-td">{item.issueDate}</td>
                      <td className="history__table-td">{item.expiryDate || '-'}</td>
                      <td className="history__table-td">
                        <span className={`history__status ${getStatusClass(item.status)}`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && historyData.length === 0 && (
            <div className="history__empty">
              <p>データがありません</p>
            </div>
          )}

          {!isLoading && historyData.length > 0 && (
            <div className="history__pagination">
              <button
                className="history__pagination-btn history__pagination-btn--nav"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                &lt;
              </button>

              {renderPagination().map((page, index) => (
                <button
                  key={index}
                  className={`history__pagination-btn ${
                    page === currentPage ? 'history__pagination-btn--active' : ''
                  } ${page === '...' ? 'history__pagination-btn--dots' : ''}`}
                  onClick={() => page !== '...' && handlePageChange(page)}
                  disabled={page === '...'}
                >
                  {page}
                </button>
              ))}

              <button
                className="history__pagination-btn history__pagination-btn--nav"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default History;
